import Grid from '@material-ui/core/Grid';
import {
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@material-ui/core';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import FormSection from '../../../components/atom/FormSection';
import { formatMoney } from '../../../utils';
import { PAYMENT_METHODS, usePaymentMethods } from '../../../hooks/usePaymentMethod';
import config from '../../../config';
import PaymentFormCard from '../../payment/components/PaymentFormCard';
import PaymentFormHold from '../../payment/components/PaymentFormHold';
import PaymentFormCash from '../../payment/components/PaymentFormCash';
import PaymentFormCheck from '../../payment/components/PaymentFormCheck';
import PaymentFormOnhouse from '../../payment/components/PaymentFormOnhouse';
import PaymentFormBalance from '../../payment/components/PaymentFormBalance';
import UniqueTicketRow from './UniqueTicketRow';
import { PaymentMethodSelect } from './PaymentMethodSelect';
import { calculateTotalDiff } from '../helpers';
import FancyCard from '../../../components/fancy/FancyCard';

const stripePromise = loadStripe(config.stripeKey);

const usePaymentSelectedMethod = (values, requiresAdditionalCharge) => {
    const allMethods = usePaymentMethods();
    const { selectedPaymentMethod, originalPaymentMethod } = useMemo(() => {
        return {
            selectedPaymentMethod:
                allMethods.find(method => method.slug === values?.force_payment_method) || null,
            originalPaymentMethod:
                allMethods.find(method => method.slug === values?.originalPaymentMethod) || null
        };
    }, [allMethods, values?.force_payment_method, values?.originalPaymentMethod]);

    // Auto-select original payment method for additional charges
    if (requiresAdditionalCharge) {
        return {
            methods: allMethods.filter(e => {
                return ![PAYMENT_METHODS.hold, PAYMENT_METHODS.terminal].includes(e.methodtype);
            }),
            selectedPaymentMethod:
                values?.force_payment_method !== 'none'
                    ? selectedPaymentMethod
                    : originalPaymentMethod
        };
    }

    return {
        methods: allMethods.filter(method =>
            [
                PAYMENT_METHODS.balance,
                PAYMENT_METHODS.check,
                PAYMENT_METHODS.cash,
                PAYMENT_METHODS.onhouse
            ].includes(method.methodtype)
        ),
        selectedPaymentMethod,
        originalPaymentMethod
    };
};

export default function BookingItemTicketSettings({
    values,
    setFieldValue,
    bookingCanModify,
    bookingItemTickets,
    setForcePaymentMethod
}) {
    // storing some state outside
    const [hasEnoughBalance, setHasEnoughBalance] = useState(false);
    const { user: currentUser } = useSelector(state => state.user);

    const totalDiff = calculateTotalDiff(values);

    const requiresAdditionalCharge = totalDiff > 0;
    const { methods, selectedPaymentMethod, originalPaymentMethod } = usePaymentSelectedMethod(
        values,
        requiresAdditionalCharge
    );
    const requiresRefund = totalDiff < 0;

    const handlePaymentMethodChange = slug => {
        // this triggers the price reload
        setForcePaymentMethod(slug);
        setFieldValue('force_payment_method', slug);
        setFieldValue('force_payment_method_payment_intent', '');
        if (slug === 'none') {
            setFieldValue('force_payment_method_note', '');
            return;
        }

        const requiresNote =
            requiresRefund ||
            (selectedPaymentMethod && selectedPaymentMethod.note_required === true);

        if (!requiresNote) {
            setFieldValue('force_payment_method_note', '');
        }

        if (selectedPaymentMethod?.methodtype === PAYMENT_METHODS.balance) {
            // Payment form will recompute availability
            return;
        }
    };

    const shouldShowPaymentNote = requiresRefund
        ? values?.force_payment_method && values.force_payment_method !== 'none'
        : selectedPaymentMethod?.note_required === true;

    const renderAdditionalChargeForm = () => {
        if (!requiresAdditionalCharge) {
            return null;
        }

        switch (selectedPaymentMethod.methodtype) {
            case PAYMENT_METHODS.card:
                return (
                    <Grid item xs={12}>
                        <PaymentFormCard
                            stripePromise={stripePromise}
                            user={currentUser}
                            note={values?.force_payment_method_note || ''}
                            setNote={note => setFieldValue('force_payment_method_note', note)}
                        />
                    </Grid>
                );
            case PAYMENT_METHODS.hold:
                return (
                    <Grid item xs={12}>
                        <PaymentFormHold
                            stripePromise={stripePromise}
                            user={currentUser}
                            note={values?.force_payment_method_note || ''}
                            setNote={note => setFieldValue('force_payment_method_note', note)}
                        />
                    </Grid>
                );
            case PAYMENT_METHODS.cash:
                return (
                    <Grid item xs={12}>
                        <PaymentFormCash
                            note={values?.force_payment_method_note || ''}
                            setNote={note => setFieldValue('force_payment_method_note', note)}
                        />
                    </Grid>
                );
            case PAYMENT_METHODS.check:
                return (
                    <Grid item xs={12}>
                        <PaymentFormCheck
                            note={values?.force_payment_method_note || ''}
                            setNote={note => setFieldValue('force_payment_method_note', note)}
                        />
                    </Grid>
                );
            case PAYMENT_METHODS.onhouse:
                return (
                    <Grid item xs={12}>
                        <PaymentFormOnhouse
                            note={values?.force_payment_method_note || ''}
                            setNote={note => setFieldValue('force_payment_method_note', note)}
                        />
                    </Grid>
                );
            case PAYMENT_METHODS.balance:
                return (
                    <Grid item xs={12}>
                        <PaymentFormBalance
                            method={selectedPaymentMethod}
                            note={values?.force_payment_method_note || ''}
                            setNote={note => setFieldValue('force_payment_method_note', note)}
                            guestId={values?.booking?.user?.id}
                            hasEnoughBalance={hasEnoughBalance}
                            setHasEnoughBalance={setHasEnoughBalance}
                            estimatedTotal={totalDiff}
                        />
                    </Grid>
                );
            default:
                return (
                    <Grid item xs={12}>
                        <small>
                            The selected payment method is not supported for booking modifications.
                            Please choose another payment method.
                        </small>
                    </Grid>
                );
        }
    };

    return (
        <FormSection title="Ticket information">
            <Grid item xs={12}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ width: 56 }}>Qty</TableCell>
                                <TableCell align="left">Ticket</TableCell>
                                <TableCell align="right">Retail per ticket</TableCell>
                                <TableCell align="right">Change</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(values?.uniqueTickets || []).map(row => (
                                <UniqueTicketRow
                                    key={`u-${row.key}`}
                                    bookingItemTickets={bookingItemTickets}
                                    u={row}
                                    disabled={!bookingCanModify}
                                    onQtyChange={qty => {
                                        setFieldValue(
                                            'uniqueTickets',
                                            (values?.uniqueTickets || []).map(u => {
                                                if (u.key === row.key) {
                                                    return { ...u, quantity: Math.max(0, qty) };
                                                }
                                                return u;
                                            })
                                        );
                                    }}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {totalDiff !== 0 && (
                    <Grid item xs={12} container spacing={2}>
                        <Grid item xs={12} style={{ marginTop: 32 }}>
                            <b>Total {totalDiff > 0 ? 'Additional Charge' : 'Refund'}:</b>{' '}
                            {formatMoney(totalDiff)}
                        </Grid>

                        <Grid item xs={6}>
                            <PaymentMethodSelect
                                methods={methods}
                                value={selectedPaymentMethod || 'none'}
                                onChange={handlePaymentMethodChange}
                                label={
                                    requiresAdditionalCharge
                                        ? 'Select payment method for additional charge'
                                        : 'Payment method used'
                                }
                                noneLabel={`Original payment method (${originalPaymentMethod?.name ||
                                    'unknown'})`}
                                showNoneOption={!requiresAdditionalCharge}
                            />
                        </Grid>

                        {shouldShowPaymentNote && !requiresAdditionalCharge && (
                            <FancyCard fullWidth>
                                <CardContent>
                                    <h3 className="mb-2">Refund note</h3>
                                    <TextField
                                        fullWidth
                                        label={requiresRefund ? 'Note' : 'Payment note'}
                                        margin="dense"
                                        value={values?.force_payment_method_note || ''}
                                        onChange={e =>
                                            setFieldValue(
                                                'force_payment_method_note',
                                                e.target.value
                                            )
                                        }
                                    />
                                </CardContent>
                            </FancyCard>
                        )}

                        {renderAdditionalChargeForm()}

                        <Grid item xs={12}>
                            <small style={{ color: '#666', fontSize: '0.7rem' }}>
                                The actual amount might differ per payment method, please check the
                                amount once the modification is complete.
                            </small>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </FormSection>
    );
}
